import { STRICTLY_NECESSARY, PERFORMANCE, FUNCTIONALITY, TARGETING, SOCIAL_MEDIA } from '../cookies';
import { getGVL, getTCModel } from './tcf-api';

/**
 * IAB TCF vendor list that meta router supports
 * @see https://docs.google.com/spreadsheets/d/1ACh9EIec_fHX2bpulU2QKDzk0FtijXWZaUZHXW6zyq4/edit?gid=0#gid=0
 */
export const IAB_SUPPORTED_VENDOR_LIST = [
  { id: 16, name: 'RtbHouse' },
  { id: 21, name: 'TheTradeDesk' },
  { id: 23, name: 'Amobee' },
  { id: 25, name: 'YahooMedia' },
  { id: 32, name: 'Xandr' },
  { id: 60, name: 'Rakuten' },
  // { id: 71, name: 'Roku' },// deletedDate 2025-09-30 in GVL
  { id: 91, name: 'Criteo' },
  { id: 130, name: 'Adroll' },
  { id: 231, name: 'AcuityAds' },
  { id: 264, name: 'Adobe' },
  // { id: 468, name: 'Neustar', altNames: ['NeustarV1'] }, // deletedDate 2025-07-01 in GVL
  { id: 565, name: 'AdobeEverestTech' },
  { id: 755, name: 'Google', altNames: ['CM360', 'GoogleAds', 'GoogleAdsSegments'] },
  { id: 759, name: 'Revjet' },
  { id: 767, name: 'Clinch' },
  { id: 804, name: 'LinkedIn' },
  // { id: 868, name: 'Convert' }, // deletedData 2023-10-09 in GVL
  { id: 907, name: 'Awin' },
  { id: 1021, name: 'RingierAxelSpringerPoland' },
  { id: 1126, name: 'Bing', altNames: ['MicrosoftAds'] },
  { id: 1127, name: 'XAds' },
];

/**
 * Map each vendor to one of the consent categories
 * that meta router supports.
 *
 * Putting all into targeting for now.
 */
export const CONSENT_IAB_VENDOR_MAP = {
  [STRICTLY_NECESSARY]: [],
  [PERFORMANCE]: [],
  [FUNCTIONALITY]: [],
  [TARGETING]: IAB_SUPPORTED_VENDOR_LIST.map(vendor => vendor.id),
  [SOCIAL_MEDIA]: [],
};

export function getSupportedIABVendorIds() {
  return IAB_SUPPORTED_VENDOR_LIST.map(vendor => vendor.id);
}

export function setVendorConsent(vendorId, consent = true) {
  const tcModel = getTCModel();

  if (!tcModel) {
    return;
  }

  if (consent) {
    tcModel.vendorConsents.set(vendorId);
  } else {
    tcModel.vendorConsents.unset(vendorId);
  }
}

export function setVendorsConsentByCookie(cookieKey, consent = true) {
  const tcModel = getTCModel();

  if (!tcModel) {
    return;
  }

  const vendorIds = Array.isArray(CONSENT_IAB_VENDOR_MAP[cookieKey]) ? CONSENT_IAB_VENDOR_MAP[cookieKey] : [];

  // opt out all by cookie key
  if (!consent) {
    tcModel.vendorConsents.unset(vendorIds);
    return;
  }

  // opt in all
  tcModel.vendorConsents.set(vendorIds);
}

export function getVendorConsent(vendorId) {
  const tcModel = getTCModel();

  if (!tcModel) {
    return false;
  }

  return tcModel.vendorConsents.has(vendorId);
}

/**
 * Helper to map IAB full vendor details to our supported vendor list and attach
 * purposes.
 */
export function getIABVendorById(id) {
  const mrSupportedVendor = IAB_SUPPORTED_VENDOR_LIST.find(vendor => vendor.id === id);

  const gvl = getGVL();

  if (gvl && gvl.vendors && gvl.vendors[id] && mrSupportedVendor) {
    const foundVendor = gvl.vendors[id];

    // attach purposes to make the UI easier to work with
    const uiPurposes = foundVendor.purposes.map(purposeId => gvl.purposes[purposeId]);
    const uiLegIntPurposes = foundVendor.legIntPurposes.map(purposeId => gvl.purposes[purposeId]);
    const uiFeatures = foundVendor.features.map(featureId => gvl.features[featureId]);
    const uiSpecialFeatures = foundVendor.specialFeatures.map(featureId => gvl.specialFeatures[featureId]);

    if (foundVendor) {
      return {
        ...mrSupportedVendor,
        ...foundVendor,
        ui: {
          iabVendorFound: true,
          purposes: uiPurposes,
          legIntPurposes: uiLegIntPurposes,
          features: uiFeatures,
          specialFeatures: uiSpecialFeatures,
        },
      };
    }
  }

  return {
    ...mrSupportedVendor,
    ui: {
      iabVendorFound: false,
    },
  };
}

export function getPurposes() {
  const gvl = getGVL();
  if (!gvl) {
    return [];
  }

  return Object.values(gvl.purposes);
}

/**
 * Opts in (or out) of both purpose consent and legitimate interest for the purposeId. This is
 * for convenience for e2e testing.
 *
 * Not all purposes can be legitimate interest, only a subset can.
 * - PurposeIds that can be leg int: 2,7,8,9,10,11
 */
export function setPurposeConsent(purposeId, consent = true) {
  const tcModel = getTCModel();

  if (!tcModel) {
    return;
  }

  if (consent) {
    tcModel.purposeConsents.set(purposeId);
    tcModel.purposeLegitimateInterests.set(purposeId);
  } else {
    tcModel.purposeConsents.unset(purposeId);
    tcModel.purposeLegitimateInterests.unset(purposeId);
  }
}

export function getPurposeConsent(purposeId) {
  const tcModel = getTCModel();

  if (!tcModel) {
    return false;
  }

  return tcModel.purposeConsents.has(purposeId);
}

export function getFeatures() {
  const gvl = getGVL();
  if (!gvl) {
    return [];
  }

  return Object.values(gvl.features);
}

export function getSpecialFeatures() {
  const gvl = getGVL();
  if (!gvl) {
    return [];
  }

  return Object.values(gvl.specialFeatures);
}

export function setSpecialFeaturesConsent(specialFeatureId, consent = true) {
  const tcModel = getTCModel();

  if (!tcModel) {
    return;
  }

  if (consent) {
    tcModel.specialFeatureOptins.set(specialFeatureId);
  } else {
    tcModel.specialFeatureOptins.unset(specialFeatureId);
  }
}

export function getSpecialFeaturesConsent(specialFeatureId) {
  const tcModel = getTCModel();

  if (!tcModel) {
    return false;
  }

  return tcModel.specialFeatureOptins.has(specialFeatureId);
}

export function acceptAll() {
  const tcModel = getTCModel();

  if (!tcModel) {
    return;
  }

  // opt in all vendors
  const vendorIds = getSupportedIABVendorIds();
  tcModel.vendorConsents.set(vendorIds);

  // opt in all purposes and leg int purposes
  const purposes = getPurposes();
  const purposeIds = purposes.map(purpose => purpose.id);
  tcModel.purposeConsents.set(purposeIds);
  tcModel.purposeLegitimateInterests.set(purposeIds);

  // opt in all special features
  const specialFeatures = getSpecialFeatures();
  const specialFeatureIds = specialFeatures.map(feature => feature.id);
  tcModel.specialFeatureOptins.set(specialFeatureIds);
}

export function rejectAll() {
  const tcModel = getTCModel();

  if (!tcModel) {
    return;
  }

  // opt out all vendors
  tcModel.vendorConsents.empty();
  tcModel.purposeConsents.empty();
  tcModel.purposeLegitimateInterests.empty();
  tcModel.specialFeatureOptins.empty();
}
